import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";

import { HASH_KEY } from "modules/utils/Constants";
import { HashIcon, PencilIcon } from "modules/icons";
import useBurstQuery from "modules/hooks/use-burst-query";
import {
  InternalAdminTagCategoryProfileFragment,
  InternalAdminTagCategoryProfileQuery,
  InternalAdminTagCategoryProfileQueryVariables,
} from "gql/graphql";
import INTERNAL_ADMIN_TAG_CATEGORY_PROFILE_QUERY from "../_networking/internal-admin-tag-category-profile";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  tagCategoryId: string;
}

const InternalAdminTagCategoryDrawer = ({ tagCategoryId }: IProps) => {
  const { openModal } = useOverlay();
  const [tagCategory, setTagCategory] =
    useState<InternalAdminTagCategoryProfileFragment | null>();

  const tags = tagCategory?.tagList ?? [];

  const [internalAdminTagCategoryProfileAPI] = useBurstQuery<
    InternalAdminTagCategoryProfileQuery,
    InternalAdminTagCategoryProfileQueryVariables
  >(INTERNAL_ADMIN_TAG_CATEGORY_PROFILE_QUERY);

  const getTagCategory = useCallback(async () => {
    if (tagCategoryId) {
      const response = await internalAdminTagCategoryProfileAPI({
        variables: { id: tagCategoryId },
      });
      if (response.data?.internalAdminTagCategory.length === 1) {
        setTagCategory(response.data.internalAdminTagCategory[0]);
      }

      return;
    }
    setTagCategory(undefined);
  }, [internalAdminTagCategoryProfileAPI, tagCategoryId]);

  useEffect(() => {
    getTagCategory();
  }, [getTagCategory]);

  const onEditClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_TAG_CATEGORY,
      id: tagCategoryId,
      onCloseCallback: getTagCategory,
    });
  }, [getTagCategory, openModal, tagCategoryId]);

  return (
    <DrawerContent>
      <DrawerCloseButton right={0} left={1} />
      <DrawerHeader>
        <Center fontWeight="bold">Tag Category Details</Center>
        <Center height={10}>
          <Divider />
        </Center>
      </DrawerHeader>
      <DrawerBody>
        {!tagCategory ? (
          <Spinner size="xl" />
        ) : (
          <VStack w="full" alignItems="flex-start">
            <HStack w="full" spacing={0}>
              <Text fontSize={28} fontWeight={700}>
                {tagCategory.name}
              </Text>
              <Spacer />
              <IconButton
                aria-label="edit tag category"
                variant="ghost"
                onClick={onEditClicked}
                icon={<PencilIcon boxSize={10} fill={colors.grayPrimary} />}
              />
            </HStack>

            {tags.length > 0 && (
              <>
                <HStack>
                  <HashIcon />
                  <Text fontWeight={700}>
                    Tags ({tagCategory.tagList.length})
                  </Text>
                </HStack>
                <Divider borderColor={colors.black} />
                <Text fontSize={11} color={colors.grayMain} pb={2}>
                  Tags within this category
                </Text>
                {[...tags]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((tag) => (
                    <React.Fragment key={tag.id}>
                      <Text
                        color={colors.gray50}
                        fontSize={12}
                        fontWeight={700}
                      >
                        {`${HASH_KEY} ${tag.name}`}
                      </Text>
                      <Divider />
                    </React.Fragment>
                  ))}
              </>
            )}
          </VStack>
        )}
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(InternalAdminTagCategoryDrawer);
